// @flow
import React, { Fragment } from 'react';
import { FormikProvider } from "formik";
import { useTranslation } from "react-i18next";
import { PAGE_LOGIN } from "../../locales/pages/namespaces";
import { Link as RouterLink } from 'react-router-dom'
import { ROUTE_PAGE_HOME } from "../../routers/routes";

import {
	Grid,
	OutlinedInput,
	Stack,
	Link,
	FormHelperText,
	Typography,
	Button,
	InputAdornment
} from "@mui/material";
import EmailIcon from "../../assets/images/icons/login/emailIcon";
import Divider from "@mui/material/Divider";
import OTP from "../../thinkPlus/components/elements/OtpField";
import { OTP_LENGTH } from "../../thinkPlus/components/wizard/constants";
import CountDownTimer from "../../thinkPlus/components/elements/CountDownTimer";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { getRouteUrl } from "../../helpers/getRouteUrl";
import './fastLoginForm.scss';

type Props = {
	formik: Object,
	loading: boolean,
	showOtp: boolean,
	otpVerified: boolean,
	countdownTrigger: number,
	onResendCode: Function
};

export const FastLoginForm = ({ formik, loading, showOtp, otpVerified, countdownTrigger, onResendCode }: Props) => {
	const { t } = useTranslation(PAGE_LOGIN)

	return (
		<Fragment>
			<FormikProvider value={formik}>
				<form className="fast-login-form" onSubmit={formik.handleSubmit} noValidate autoComplete="off">
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Stack spacing={1}>
								<OutlinedInput
									id="email"
									value={formik.values.email}
									name="email"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder={t("Email")}
									fullWidth
									error={Boolean(formik.touched.email && formik.errors.email)}
									autoComplete={'email'}
									className='border-neutral-dark-radius'
									sx={{ minHeight: 'unset' }}
									startAdornment={
										<InputAdornment
											position="start"
											sx={{ bgColor: '#FFFFFF' }}>
											<EmailIcon />
										</InputAdornment>
									}
									inputProps={{
										style: {
											fontSize: '14px',
											padding: '10px',
											minHeight: 'unset'
										},
									}}
								/>
								{formik.touched.email && formik.errors.email && (
									<FormHelperText error id="standard-weight-helper-text-email-fast-login">
										{formik.errors.email}
									</FormHelperText>
								)}
							</Stack>
						</Grid>
						{showOtp && (
							<Grid item xs={12}>
								<Stack spacing={1}>
									<OTP
										value={formik.values.otp}
										onChange={(value) => {
											const next = typeof value === 'function'
												? value(formik.values.otp)
												: value;
											formik.setFieldValue('otp', next);
										}}
										length={OTP_LENGTH}
									/>
									{formik.touched.otp && formik.errors.otp && (
										<FormHelperText error id="standard-weight-helper-text-otp-fast-login">
											{formik.errors.otp}
										</FormHelperText>
									)}
								</Stack>
							</Grid>
						)}
						{(showOtp || otpVerified) && (
							<Grid item xs={12}>
								<Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
									{showOtp && (
										<Typography variant="caption" sx={{ color: 'text.secondary' }}>
											{t("The code will be active for")}{" "}
											<CountDownTimer key={countdownTrigger} minutes={5} />
										</Typography>
									)}
									{onResendCode && (
										<Button
											type="submit"
											onClick={onResendCode}
											className='button-primary'
											fullWidth
										>
											{t('Resend')}
										</Button>
									)}
								</Stack>
							</Grid>
						)}
						<Grid item xs={12} textAlign={'center'}>
							<Button
								disableElevation
								disabled={loading || (otpVerified
									? false
									: (showOtp
										? (Boolean(formik.errors.otp) || !formik.values.otp)
										: (Boolean(formik.errors.email) || !formik.values.email)))}
								fullWidth
								type="submit"
								sx={{ p: '14px 0' }}
								size={"small"}
								variant={"contained"}
								className='button-primary'
							>
								{(showOtp || otpVerified) ? t('Login') : t('Send code')}
							</Button>
							<Divider
								className="fast-login-divider"
								orientation="vertical"
								flexItem
								sx={{
									borderTop: '1px solid #eaeef7;',
									marginBottom: '16px'
								}}
							/>
							<Typography variant="h5" sx={{ margin: '0 0 20px 0' }} component="span">
								<Link
									component={RouterLink}
									variant="h5"
									to={getRouteUrl(ROUTE_PAGE_HOME)}
									underline="hover"
									className='color-default p-sm flex-center back-link-arrow'
								>
									<ArrowBackIosNewIcon fontSize="small" sx={{  marginRight: '6px', width: '10px'}} /> {t('Return')}
								</Link>
							</Typography>
						</Grid>
					</Grid>
				</form>
			</FormikProvider>
		</Fragment>
	);
};
